import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useLogin } from "@/lib/api";
import { logger } from "@/lib/logger";

// Constant-length JSX expression keeps prettier output stable for any rendered name.
const PROJECT_NAME = "MPC Simulator";

export function LoginPage() {
  const navigate = useNavigate();
  const loginMutation = useLogin();
  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    logger.debug("[login] submitting for", email);
    try {
      const result = await loginMutation.mutateAsync({ email, password, rememberMe });
      logger.debug("[login] success → redirecting to /home", result);
      navigate("/home");
    } catch (err) {
      const status = (err as { status?: number } | undefined)?.status;
      const detail = (err as { body?: { detail?: string } } | undefined)?.body?.detail;
      if (status === 403 && detail === "email_not_verified") {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
        return;
      }
      logger.debug("[login] failed:", err);
      setError(status === 401 ? "Invalid credentials." : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md border-white/70 shadow-xl shadow-slate-900/10">
        <CardHeader className="space-y-4 text-center">
          <img src="/brand/logo.png" alt="VELUX" className="mx-auto h-12 w-auto" />
          <div className="space-y-2">
            <CardTitle className="font-heading text-primary text-2xl font-bold">
              {PROJECT_NAME}
            </CardTitle>
            <p className="text-muted text-sm">
              Smart climate control for daylight-first home installations
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-foreground text-sm font-medium">
                Password
              </label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="Enter password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-muted hover:text-foreground absolute top-1/2 right-3 -translate-y-1/2 transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="rememberMe" className="text-foreground text-sm">
                Remember me for 30 days
              </label>
            </div>
            {error && <p className="text-destructive text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={loginMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
            >
              {loginMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in…
                </>
              ) : (
                "Sign in"
              )}
            </Button>
            <p className="text-muted text-center text-sm">
              Don&apos;t have an account?{" "}
              <Link to="/signup" className="text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
